import { motion } from "motion/react";
import { type Lang, type Translations } from "@/lib/i18n";

// CTA path-with-lang: see Hero.tsx for rationale.
const appUrl = (lang: Lang): string =>
  lang === "zh" ? "/app/register?lang=zh" : "/app/register";

export const Pricing = ({ scrollTo, tr, lang }: { scrollTo: (id: string) => void; tr: Translations; lang: Lang }) => {
  // Pro subscription (index 1) is the recommended tier
  const recommendedSubIdx = 1;

  return (
    <section id="pricing" className="py-32 px-6 md:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-normal mb-6 font-landing-serif">{tr.pricing.title}</h2>
          <p className="text-black/50 label-vogue">{tr.pricing.subtitle}</p>
        </div>

        {/* Subscriptions */}
        <h3 className="text-sm label-vogue text-black/40 mb-8 text-center">{tr.pricing.subscriptionsLabel}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20 items-end">
          {tr.pricing.subscriptions.map((plan, i) => {
            const isPro = i === recommendedSubIdx;

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1, type: "spring", stiffness: 100 }}
                whileHover={{ scale: isPro ? 1.04 : 1.02 }}
                className={`p-10 flex flex-col justify-between rounded-2xl relative ${
                  isPro
                    ? "bg-gradient-to-br from-[#f43f5e] to-[#8b5cf6] text-white shadow-2xl shadow-[#f43f5e]/20 md:scale-105 z-10"
                    : "bg-[#f9f9f9] border border-black/5 text-[#2d3435]"
                }`}
              >
                {isPro && (
                  <motion.div
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -top-3 left-0 right-0 mx-auto w-fit bg-white text-[#f43f5e] text-[10px] label-vogue px-4 py-1 rounded-full whitespace-nowrap font-semibold shadow-md"
                  >
                    {tr.pricing.bestValue}
                  </motion.div>
                )}
                <div>
                  <h3 className={`text-2xl mb-2 font-landing-sans ${isPro ? "text-white" : ""}`}>{plan.name}</h3>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className={`text-4xl font-landing-serif ${isPro ? "text-white" : ""}`}>{plan.price}</span>
                    <span className={`text-sm ${isPro ? "text-white/70" : "text-black/40"}`}>{tr.pricing.perMonth}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-6">
                    <span className={`text-xs font-medium ${isPro ? "text-white/90" : "text-landing-accent"}`}>Save {plan.discount}%</span>
                    <span className={`text-xs ${isPro ? "text-white/40" : "text-black/30"}`}>·</span>
                    <span className={`text-xs ${isPro ? "text-white/70" : "text-black/50"}`}>{plan.credits} {tr.pricing.creditsPerMonth}</span>
                  </div>
                  <p className={`text-sm mb-8 leading-relaxed ${isPro ? "text-white/80" : "text-black/60"}`}>{plan.desc}</p>
                  <ul className="space-y-3 mb-10">
                    {plan.features.map((f, j) => (
                      <li key={j} className="text-xs label-vogue flex items-center gap-3">
                        <div className={`w-1 h-1 rounded-full ${isPro ? "bg-white" : "bg-landing-accent"}`}></div> {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <a
                  href={appUrl(lang)}
                  className={`w-full py-4 label-vogue transition-all block text-center no-underline rounded-xl ${
                    isPro
                      ? "bg-white text-[#f43f5e] hover:bg-white/90 cursor-pointer font-semibold"
                      : "bg-black text-white hover:bg-black/90 cursor-pointer"
                  }`}
                >
                  {plan.cta}
                </a>
              </motion.div>
            );
          })}
        </div>

        {/* Credit Packs */}
        <h3 className="text-sm label-vogue text-black/40 mb-8 text-center">{tr.pricing.packsLabel}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto items-end">
          {tr.pricing.packs.map((pack, i) => {
            const isRec = pack.recommended;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08, type: "spring", stiffness: 120 }}
                whileHover={{ scale: isRec ? 1.04 : 1.02 }}
                className={`p-8 rounded-2xl flex flex-col justify-between relative ${
                  isRec
                    ? "bg-gradient-to-br from-[#f43f5e] to-[#8b5cf6] text-white shadow-2xl shadow-[#f43f5e]/20 md:scale-105 z-10"
                    : "border border-brand-orange/30 bg-white"
                }`}
              >
                {isRec && (
                  <motion.div
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -top-3 left-0 right-0 mx-auto w-fit bg-white text-[#f43f5e] text-[10px] label-vogue px-4 py-1 rounded-full whitespace-nowrap font-semibold shadow-md"
                  >
                    {tr.pricing.easyStart}
                  </motion.div>
                )}
                <div>
                  <h4 className={`text-lg mb-2 font-landing-sans ${isRec ? "text-white" : ""}`}>{pack.name}</h4>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className={`text-3xl font-landing-serif ${isRec ? "text-white" : ""}`}>{pack.price}</span>
                  </div>
                  <p className={`text-xs mb-6 ${isRec ? "text-white/70" : "text-black/50"}`}>{pack.credits} {tr.pricing.creditsLabel} · {tr.pricing.oneYearValidity}</p>
                </div>
                <a
                  href={appUrl(lang)}
                  className={`w-full py-3 label-vogue transition-all block text-center no-underline text-sm rounded-xl ${
                    isRec
                      ? "bg-white text-[#f43f5e] hover:bg-white/90 cursor-pointer font-semibold"
                      : "text-brand-orange bg-orange-50 hover:bg-orange-100 cursor-pointer"
                  }`}
                >
                  {pack.cta}
                </a>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
